// src/data/legal.js
// All legal text for Eye on Storm.
// Single source of truth — every disclaimer, limitation, and notice
// in the app imports from here.
//
// IMPORTANT: Review with a licensed Louisiana attorney before public launch.
// Last reviewed: [DATE]. Louisiana law governs.

// ── App identity ──────────────────────────────────────────────────
export const APP_LEGAL_NAME = 'Eye on Storm';
export const APP_OPERATOR   = 'Sheldon Alex Harris';   // Operator of Eye on Storm
export const APP_STATE      = 'Louisiana';
export const CONTACT_EMAIL  = 'legal@eyeonstorm.la';       // FILL IN BEFORE LAUNCH

// ── Emergency disclaimer ──────────────────────────────────────────
export const EMERGENCY_DISCLAIMER = {
  headline: 'This app is NOT a 911 service',

  body: `For any life-threatening emergency, call 911 immediately.

Eye on Storm is a community information tool. It is not monitored by emergency services, law enforcement, fire departments, or any government agency. No one at Eye on Storm will dispatch help to you.

SOS reports you submit are shared with your neighbors — they are not received by 911, police, fire, or EMS.`,

  callouts: [
    '🚨  Call 911 for any life-threatening emergency',
    '📵  This app is NOT monitored 24/7',
    '🚫  SOS does NOT reach official responders',
    '⏱  There is no guaranteed response time',
  ],

  affirmation: 'I understand this is a community tool, not an emergency service. I will call 911 in emergencies.',
};

// ── Accuracy disclaimer ───────────────────────────────────────────
// Shown on every app load (compact bar) and beneath every data section.
// Addresses the "not professional advice" exposure and inaccurate data liability.
export const ACCURACY_DISCLAIMER = {
  // Shown as compact persistent bar on every screen after onboarding
  short: 'Community reports may be unverified',

  // Shown beneath alert/data sections
  medium: 'Information on this app is community-reported and may be inaccurate, incomplete, or outdated. It is not a substitute for official emergency management guidance, professional medical advice, legal advice, or engineering/structural assessments. Always verify through official sources before making any safety, medical, or evacuation decision.',

  // Shown on News/Alerts page beneath NWS data
  nwsSpecific: 'Weather data sourced from the National Weather Service. Eye on Storm is not affiliated with NWS or NOAA. Data may be delayed or incomplete. Monitor weather.gov/lix for authoritative information.',

  // Shown on First Aid page
  medicalSpecific: 'This information is for general reference only and is NOT a substitute for professional medical care. In any medical emergency, call 911. Follow guidance from trained medical professionals, not this app.',

  // Shown on Evacuation/Traffic page
  evacuationSpecific: 'Road conditions and evacuation routes are community-reported and sourced from DOTD 511LA. Conditions change rapidly. Follow instructions from law enforcement, fire departments, and official emergency management. Do not rely solely on this app for evacuation decisions.',

  // Shown on Gas page
  gasSpecific: 'Gas station status is community-reported and unverified. Verify before driving. Status may have changed since last report.',

  // Shown on Shelter page
  shelterSpecific: 'Shelter status is sourced from Red Cross and community reports. Capacity and availability change rapidly. Contact shelters directly or call 211 before traveling.',
};

// ── Data accuracy disclaimer ──────────────────────────────────────
export const DATA_DISCLAIMER = {
  headline: 'Community-reported information',

  body: `All incident reports, road conditions, gas station status, shelter availability, and connectivity information on this app are submitted by community members and automated data feeds. This information:

• Has not been verified by Eye on Storm
• May be inaccurate, outdated, or incomplete
• Should not be relied upon as your sole source of information
• Changes rapidly during emergencies

Always verify critical information through official sources before making safety decisions.`,

  officialSources: [
    { name: 'NWS New Orleans',  url: 'https://weather.gov/lix' },
    { name: 'Louisiana GOHSEP', url: 'https://gohsep.la.gov' },
    { name: 'DOTD 511LA',       url: 'https://511la.org' },
    { name: 'NOLA Ready',       url: 'https://ready.nola.gov' },
  ],
};

// ── Full Terms of Service ─────────────────────────────────────────
export const TERMS_OF_SERVICE = {
  lastUpdated: '2026-05-27',
  sections: [
    {
      id: 'acceptance',
      title: '1. Acceptance of Terms',
      body: `By accessing or using Eye on Storm ("the App"), you agree to be bound by these Terms of Service ("Terms"). If you do not agree to these Terms, do not use the App.

These Terms constitute a legally binding agreement between you and ${APP_OPERATOR} ("Operator"), the individual or organization that operates Eye on Storm.`,
    },
    {
      id: 'not_emergency',
      title: '2. This Is Not an Emergency Service',
      body: `THE APP IS NOT A 911 SERVICE, EMERGENCY DISPATCH SYSTEM, OR SUBSTITUTE FOR OFFICIAL EMERGENCY SERVICES.

The App does not connect you to law enforcement, fire departments, emergency medical services, or any governmental emergency management agency. The Operator does not monitor the App 24 hours a day, 7 days a week. There is no guaranteed response time to any report, SOS, or communication submitted through the App.

IN ANY LIFE-THREATENING EMERGENCY, CALL 911 IMMEDIATELY. Do not rely on the App as your primary means of requesting emergency assistance.`,
    },
    {
      id: 'not_professional_advice',
      title: '3. Not Professional Advice',
      body: `THE APP DOES NOT PROVIDE PROFESSIONAL ADVICE OF ANY KIND.

Information displayed in the App is NOT a substitute for:

• Professional medical advice, diagnosis, or treatment — always seek the advice of your physician or qualified health provider for any medical questions or emergencies
• Legal advice — consult a licensed attorney for legal questions
• Engineering or structural safety assessments — do not re-enter damaged buildings based on information from this App
• Official emergency management guidance — follow instructions from FEMA, Louisiana GOHSEP, local emergency management, law enforcement, and fire departments

The first aid information, evacuation guidance, shelter locations, and all other content in this App are provided for general informational purposes only. They may be incomplete, outdated, or inapplicable to your specific situation.

RELIANCE ON ANY INFORMATION PROVIDED BY THIS APP IS SOLELY AT YOUR OWN RISK.`,
    },
    {
      id: 'community_content',
      title: '4. Community-Reported Information',
      body: `The App displays information submitted by community members ("User Content") and data retrieved from automated third-party feeds (National Weather Service, USGS, Louisiana DOTD, and others). The Operator does not verify the accuracy, completeness, or timeliness of any User Content or third-party data.

User Content and automated data may be:
• Inaccurate, outdated, incomplete, or misleading
• Based on misidentified locations or conditions
• Submitted by users who are themselves misinformed
• Delayed due to network or system issues

You assume all risk associated with relying on User Content or third-party data displayed in the App.`,
    },
    {
      id: 'no_warranty',
      title: '5. Disclaimer of Warranties',
      body: `THE APP IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, ACCURACY, RELIABILITY, OR NON-INFRINGEMENT.

The Operator does not warrant that:
• The App will be available, uninterrupted, or error-free
• Information displayed will be accurate or current
• The App will function during or after a disaster event
• SMS alerts will be delivered to any specific device
• Push notifications will be received in a timely manner

The App relies on third-party infrastructure (cellular networks, internet connectivity, Firebase, Twilio) that the Operator does not control and cannot guarantee.`,
    },
    {
      id: 'limitation',
      title: '6. Limitation of Liability',
      body: `TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, THE OPERATOR SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING BUT NOT LIMITED TO:

• Personal injury or death resulting from reliance on App information
• Property damage resulting from reliance on App information
• Loss of life, health, or safety
• Decisions made based on inaccurate, delayed, or unavailable information
• Failure to receive alerts, notifications, or SOS acknowledgments
• Failure of third-party services (SMS, push notifications, internet)
• Reliance on first aid, evacuation, shelter, or medical information in the App

IN STATES THAT DO NOT ALLOW LIMITATION OF LIABILITY FOR PERSONAL INJURY, THE OPERATOR'S LIABILITY IS LIMITED TO THE MAXIMUM EXTENT PERMITTED BY LAW.

LOUISIANA RESIDENTS: Some limitations above may not apply under Louisiana law. Nothing in these Terms waives rights you may have under Louisiana law that cannot be waived by contract.`,
    },
    {
      id: 'user_responsibilities',
      title: '7. Your Responsibilities',
      body: `By using the App, you agree that you will:

• Call 911 for any life-threatening emergency — do not rely solely on the App
• Verify critical information through official channels before making safety decisions
• Consult qualified professionals (doctors, engineers, attorneys) for professional advice
• Not submit false or misleading reports
• Not use the App for any unlawful purpose
• Not submit personal information of others without their consent
• Treat other community members with respect

You understand that SOS reports you submit are shared publicly with other app users, not with emergency services.`,
    },
    {
      id: 'sos_specific',
      title: '8. SOS Feature Specific Terms',
      body: `The SOS feature allows you to share a distress signal with other app users in your area. YOU MUST UNDERSTAND THE FOLLOWING BEFORE USING SOS:

• SOS reports are NOT received by 911, police, fire, or emergency medical services
• The Operator does not monitor SOS reports or dispatch any response
• Other app users who see your SOS are volunteers with no obligation to respond
• There is no guaranteed response time — or any response at all
• SMS SOS sent to the app's phone number is NOT a 911 call

If you are in a life-threatening emergency:
1. Call 911 first
2. Only then, if safe to do so, submit an SOS through the App

The App's SOS feature is intended as a supplemental community awareness tool only.`,
    },
    {
      id: 'sms_terms',
      title: '9. SMS Alerts',
      body: `By enrolling a phone number for SMS alerts, you agree that:

• Standard message and data rates may apply
• Message frequency will vary based on emergency conditions
• You can reply STOP to cancel at any time
• The Operator is not liable for failed, delayed, or undelivered messages
• SMS delivery depends on carrier networks outside the Operator's control
• During widespread emergencies, SMS networks may be congested or unavailable

SMS alerts are a supplement to — not a replacement for — official emergency alert systems (IPAWS/Wireless Emergency Alerts).`,
    },
    {
      id: 'official_sources',
      title: '10. Official Emergency Information',
      body: `Eye on Storm is not affiliated with any government agency. For official emergency information, always consult:

• National Weather Service New Orleans: weather.gov/lix
• Louisiana GOHSEP: gohsep.la.gov
• NOLA Ready (City of New Orleans): ready.nola.gov
• Louisiana 511: 511la.org or 511
• Wireless Emergency Alerts (WEA): enabled by default on your phone
• NOAA Weather Radio: broadcast 24/7 on battery-powered radio

During power or internet outages, a battery-powered or hand-crank NOAA Weather Radio is the most reliable source of emergency information.`,
    },
    {
      id: 'data_privacy',
      title: '11. Data and Privacy',
      body: `Location data: GPS coordinates you share via SOS or reports are stored and displayed to other app users in your area. Do not share your location if you do not consent to this.

Phone numbers: If you enroll for SMS alerts, your verified phone number is stored in our database. It is not sold to third parties. It is used only to send emergency alerts. You can request deletion by emailing ${CONTACT_EMAIL}.

Community reports: Reports you submit (including body text and approximate location) are visible to other app users. Do not include sensitive personal information in report text.

No account required: The App does not require account creation. No name or email is collected unless you voluntarily provide it.`,
    },
    {
      id: 'changes',
      title: '12. Changes to Terms',
      body: `The Operator may update these Terms at any time. Continued use of the App after changes constitutes acceptance of the updated Terms. Material changes will be noted in the app on next launch.`,
    },
    {
      id: 'governing_law',
      title: '13. Governing Law',
      body: `These Terms are governed by the laws of the State of Louisiana, without regard to its conflict of law provisions. Any disputes shall be resolved in the courts of Orleans Parish, Louisiana, or in federal court in the Eastern District of Louisiana.`,
    },
    {
      id: 'contact',
      title: '14. Contact',
      body: `Questions about these Terms: ${CONTACT_EMAIL}

To report inaccurate information, abusive content, or technical issues, use the feedback button in the app or email ${CONTACT_EMAIL}.

This app is operated as a community service. The Operator is an individual/small organization, not a corporation. Thank you for using Eye on Storm responsibly.`,
    },
  ],
};

// ── Contextual banners (per feature) ─────────────────────────────
export const CONTEXTUAL_NOTICES = {
  feed:         'Community-reported · Not independently verified',
  traffic:      'Community + DOTD data · Not official · Verify before driving · Follow law enforcement orders',
  gas:          'Community-reported · Unverified · Status may have changed — verify before driving',
  shelters:     'Status from Red Cross + community · Not guaranteed · Call 211 or verify before traveling',
  firstAid:     'General reference only · NOT professional medical advice · Call 911 for emergencies',
  hotspots:     'Community-estimated · Not carrier-verified · Confirm before depending on connectivity',
  weather:      'Source: NWS · May be delayed · Not affiliated with NWS · Monitor weather.gov/lix',
  connectivity: 'Community-estimated signal · Not carrier-verified · May be inaccurate',
  evacuation:   'Source: DOTD 511LA · Conditions change rapidly · Follow official evacuation orders',
  news:         'NWS data may be delayed · Not affiliated with NWS/NOAA · Verify at weather.gov/lix',
};

// ── Onboarding acceptance ─────────────────────────────────────────
export const DISCLAIMER_ACCEPTED_KEY = 'eos-disclaimer-accepted-v4';  // v4 = rebrand to Eye on Storm  // bumped — shows to all existing users
export const DISCLAIMER_VERSION       = 4;  // bumped for Eye on Storm rebrand
